import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App as AntdApp, Button, Card, Form, Input, Typography } from 'antd';
import { ArrowLeftOutlined, MailOutlined, NumberOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import AuthBrandPanel from '../components/auth/AuthBrandPanel';
import { AUTH_PAGE_STYLES } from '../components/auth/authPageStyles';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteBranding } from '../hooks/useSiteBranding';
import { authAPI } from '../services/api';
import { safeErrorMessage } from '../utils/errorHandler';

const { Title, Text } = Typography;

type AlertState = {
  type: 'success' | 'info' | 'error';
  message: string;
};

const VerifyEmail: React.FC = () => {
  const { message } = AntdApp.useApp();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const { slogan, siteName, userLoginLogoUrl } = useSiteBranding();
  const token = searchParams.get('token') || '';
  const email = useMemo(() => searchParams.get('email') || '', [searchParams]);
  const [alertState, setAlertState] = useState<AlertState | null>(
    token ? { type: 'info', message: '正在校验邮箱激活链接...' } : null,
  );

  useEffect(() => {
    setLogoFailed(false);
  }, [userLoginLogoUrl]);

  useEffect(() => {
    form.setFieldsValue({ email });
  }, [email, form]);

  useEffect(() => {
    if (!token) return;
    let active = true;

    const verifyByToken = async () => {
      try {
        const result = await authAPI.verifyEmail({ token });
        if (!active) return;
        setAlertState({ type: 'success', message: result.message || '邮箱验证成功，请登录。' });
        message.success(result.message || '邮箱验证成功，请登录。');
        window.setTimeout(() => {
          navigate('/login?verified=1', { replace: true });
        }, 900);
      } catch (err: any) {
        if (!active) return;
        setAlertState({
          type: 'error',
          message: safeErrorMessage(err, '激活链接无效或已过期，请改用验证码验证。'),
        });
      }
    };

    verifyByToken();
    return () => {
      active = false;
    };
  }, [message, navigate, token]);

  const handleVerify = async (values: { email: string; code: string }) => {
    setLoading(true);
    try {
      const result = await authAPI.verifyEmail(values);
      message.success(result.message || '邮箱验证成功，请登录。');
      navigate('/login?verified=1', { replace: true });
    } catch (err: any) {
      setAlertState({
        type: 'error',
        message: safeErrorMessage(err, '验证码验证失败，请稍后重试。'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const currentEmail = form.getFieldValue('email') || email;
    if (!currentEmail) {
      message.warning('请先输入注册邮箱');
      return;
    }

    setResending(true);
    try {
      const result = await authAPI.resendVerification(currentEmail);
      setAlertState({ type: 'info', message: result.message || '激活邮件已重新发送。' });
      message.success(result.message || '激活邮件已重新发送。');
    } catch (err: any) {
      setAlertState({
        type: 'error',
        message: safeErrorMessage(err, '激活邮件发送失败，请稍后重试。'),
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={`auth-page ${mode === 'dark' ? 'is-dark' : ''}`}>
      <style>{AUTH_PAGE_STYLES}</style>

      <AuthBrandPanel
        description={slogan}
        siteName={siteName}
        logoUrl={userLoginLogoUrl}
        logoFailed={logoFailed}
        onLogoError={() => setLogoFailed(true)}
      />

      <div className="auth-page-right">
        <div className="auth-shell">
          <Card className="auth-card" bordered={false}>
            <div className="auth-topbar">
              <div className="auth-badge">
                <SafetyCertificateOutlined />
                <span>邮箱验证</span>
              </div>
              <Link to="/login" className="auth-back-link">
                <ArrowLeftOutlined style={{ marginRight: 6 }} />
                返回登录
              </Link>
            </div>

            <div className="auth-header">
              <Title className="auth-title">验证邮箱</Title>
              <Text className="auth-subtitle">
                输入注册邮箱和验证码，或直接使用邮件中的激活链接完成账号启用。
              </Text>
            </div>

            {alertState ? (
              <Alert
                type={alertState.type}
                message={alertState.message}
                showIcon
                style={{ marginBottom: 20, borderRadius: 14 }}
              />
            ) : null}

            <Form form={form} onFinish={handleVerify} size="large" className="auth-form" layout="vertical">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: '请输入注册邮箱' },
                  { type: 'email', message: '请输入有效邮箱' },
                ]}
              >
                <Input className="auth-input" prefix={<MailOutlined />} placeholder="注册邮箱" autoComplete="email" />
              </Form.Item>

              <Form.Item
                name="code"
                rules={[
                  { required: true, message: '请输入验证码' },
                  { len: 6, message: '验证码为 6 位数字' },
                ]}
              >
                <Input className="auth-input" prefix={<NumberOutlined />} placeholder="6 位邮箱验证码" maxLength={6} />
              </Form.Item>

              <Form.Item style={{ marginBottom: 12 }}>
                <Button className="auth-submit" type="primary" htmlType="submit" loading={loading} block>
                  完成验证
                </Button>
              </Form.Item>
            </Form>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <Button onClick={handleResend} loading={resending} block>
                重新发送验证码
              </Button>
              <Button type="link" block onClick={() => navigate('/register')}>
                返回注册
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
